import React from 'react'
import { Line } from 'react-chartjs-2'
import { Card, CardContent } from '@mui/material'
import AnalysisEmptyState from './AnalysisEmptyState'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
)

interface LineChartData {
  labels: string[]
  datasets: {
    label: string
    data: number[]
    borderColor: string
    backgroundColor: string
  }[]
}

interface LineChartProps {
  title: string
  data: LineChartData
  embedded?: boolean
}

const LineChart: React.FC<LineChartProps> = ({
  title,
  data,
  embedded = false,
}) => {
  const isSingleElement = data.labels.length === 1
  const updatedData = isSingleElement
    ? {
        labels: ['', ...data.labels, ''],
        datasets: data.datasets.map((dataset) => ({
          ...dataset,
          data: [dataset.data[0], dataset.data[0], dataset.data[0]],
          pointRadius: [0, 3, 0],
          borderColor: dataset.borderColor,
        })),
      }
    : data

  const hasData =
    updatedData.labels.length > 0 &&
    updatedData.datasets.some((dataset) => dataset.data.length > 0)

  const chartBody = hasData ? (
    <div className="ma-line-layout">
      <div className="ma-chart-canvas ma-chart-canvas--line">
        <Line
          data={updatedData}
          options={{
            maintainAspectRatio: false,
            responsive: true,
            plugins: {
              legend: { display: false },
            },
            scales: {
              x: {
                type: 'category',
                ticks: {
                  autoSkip: true,
                  color: '#5f7484',
                  font: { size: 11, family: 'Lato, sans-serif' },
                },
                grid: { display: false },
              },
              y: {
                beginAtZero: true,
                ticks: {
                  color: '#8a9ba7',
                  font: { size: 11, family: 'Lato, sans-serif' },
                  precision: 0,
                },
                grid: { color: '#eaf0f0' },
              },
            },
          }}
        />
      </div>
      <ul className="ma-bar-legend">
        {data.datasets.map((ds) => (
          <li key={ds.label} className="ma-bar-legend-item">
            <span
              className="ma-bar-legend-swatch"
              style={{ background: ds.borderColor }}
            />
            <span className="ma-bar-legend-label">{ds.label}</span>
          </li>
        ))}
      </ul>
    </div>
  ) : (
    <AnalysisEmptyState />
  )

  if (embedded) {
    return <div>{chartBody}</div>
  }

  return (
    <Card
      style={{
        height: '100%',
        width: '100%',
        maxWidth: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardContent
        style={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}
      >
        <h5 className="text-center" style={{ height: '50px' }}>
          {title}
        </h5>
        {chartBody}
      </CardContent>
    </Card>
  )
}

export default LineChart
